// services/network-summary.service.ts
import { Injectable } from '@angular/core';
import { lastValueFrom, Observable, of } from 'rxjs';
// import { VehicleData, DepotData, RegionData, UnutilisedVehicleData } from '../models/network-summary.model';
import { VehicleData } from '../models/vehicle.model';
import { DepotData } from '../models/depart-region-depot.model';
import {
  DepotUtilizationStatistics,
  LeagueDepotData,
  LeagueRegionData,
  RegionData,
  VehicleUtilizationStatistics,
  VehicleUtilizationStatisticsResponse,
  LineGraphUtilizationDataPoint
} from '../models/depot-insight.model';
import { RestProvider } from './rest.service';
import { environment } from '../../environments/environment';
import { IQueryParameters } from '../models/api-base-actions.interface';
import { ChartData } from 'chart.js';
import { IQueryFilterRange } from '../models/vehicle-status.model';
import { yearsPerPage } from '@angular/material/datepicker';



@Injectable({
  providedIn: 'root',
})
export class NetworkSummaryService {


  private baseApiUrl = environment.dataliveApiUrl;

  constructor(private rest: RestProvider) { }


  getVehicleCountData(): Promise<VehicleData[]> {
    const vehicleCountData: VehicleData[] = [
      { date: '31.07.24', vehicleCount: 10, depot: 'DPD Dartford' },
      { date: '23.07.24', vehicleCount: 8, depot: 'DPD Guildford' },
      { date: '21.07.24', vehicleCount: 5, depot: 'DPD Stoke' },
      { date: '19.08.24', vehicleCount: 2, depot: 'DPD Bristol' },
    ];

    return Promise.resolve(vehicleCountData)
  }

  getDepotData(): Promise<DepotData[]> {
    const depotData: DepotData[] = [
      { name: 'DPD Guildford', utilisation: '85%', vor: '10%', vehicleCount: 367, avg: '87%' },
      { name: 'DPD Middleton', utilisation: '84%', vor: '2%', vehicleCount: 748, avg: '90%' },
      { name: 'DPD Manchester', utilisation: '75%', vor: '0%', vehicleCount: 208, avg: '100%' },
      { name: 'DPD Bristol', utilisation: '79%', vor: '7%', vehicleCount: 50, avg: '85%' },
      { name: 'DPD Aberdeen', utilisation: '52%', vor: '30%', vehicleCount: 200, avg: '65%' },
    ];
    return Promise.resolve(depotData)
  }

  getRegionData(): Promise<RegionData[]> {

    const regionData: RegionData[] = [
      { name: 'DPD Region 3', utilisation: '90%', vor: '5%', vehicleCount: 1000, avg: '94%' },
      { name: 'DPD Region 1', utilisation: '80%', vor: '4%', vehicleCount: 670, avg: '82%' },
      { name: 'DPD Region 8', utilisation: '20%', vor: '408%', vehicleCount: 75, avg: '79%' },
      { name: 'DPD Region 5', utilisation: '62%', vor: '14%', vehicleCount: 380, avg: '65%' },
      { name: 'DPD Region 4', utilisation: '61%', vor: '35%', vehicleCount: 205, avg: '50%' },
    ];
    return Promise.resolve(regionData)
  }

  getVehicleStatusData(_params: IQueryParameters) {
    const res = this.rest.get<LeagueDepotData[]>(`${this.baseApiUrl}/utilization/unutilized_reason_statistics/`, _params ? _params : undefined)
    return lastValueFrom(res)
  }

  refactorVehicleStatusData(data: any[]) {
    const vehicleStatusData = {
      labels: [] as string[],
      datasets: [
        {
          data: [] as number[], // Example data representing VOR, In use, and Available
          backgroundColor: ["#D1D4D8", "#EAFCF6", "#98F1D0"], // Corresponding colors for VOR, In Use, Available
        },
      ],
    };

    for (const [key, value] of Object.entries(data)) {
      vehicleStatusData.labels.push(key)
      vehicleStatusData.datasets[0].data.push(value)
    }

    return vehicleStatusData;
  }



  // EXAMPLE DATAASETS
  // {
  //   label: 'Utilisation Target',
  //   data: [60, 60, 60, 60, 60, 60, 60, 60, 60, 60, 60], // Target line (e.g., 60%)
  //   borderColor: '#ddd', // Adjust color as needed
  //   borderDash: [5, 5], // Dashed line
  //   fill: false,
  // },
  // {
  //   label: 'Total Average',
  //   data: [10, 30, 40, 50, 60, 65, 80, 90, 85, 70, 50], // Example average utilization values
  //   borderColor: '#27ae60', // Green line
  //   fill: false,
  //   tension: 0.4,
  // },

  refactorUtilizationDataForLineGraph(res: any, range: IQueryFilterRange): ChartData {
    function createBlankDataPoint (): LineGraphUtilizationDataPoint {
      return <LineGraphUtilizationDataPoint>{
        utilized: 0,
        total: 0,
        available: 0,
        notAvailable: 0,
        unutilized: 0,
      }
    }

    const networkUtlisationData: ChartData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      datasets: []
    }

    // do year first
    const dataset: any = {
      label: 'Total Utilisation Average',
      data: [], // Example average utilization values
      borderColor: '#27ae60', // Green line
      fill: false,
      tension: 0.4,
    };


    if (range === IQueryFilterRange.ThisYear || !range) {
      // first go through the month and and group by month.
      const monthUtilized: { [key: string]: LineGraphUtilizationDataPoint } = {};
      let labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      for (const key of Object.keys(res)) {
        const date = new Date(key)
        const monthKey = `${date.getMonth()}`

        if (!monthUtilized[monthKey]) {
          monthUtilized[monthKey] = createBlankDataPoint();
        }

        monthUtilized[monthKey].utilized += res[key].utilized_count;
        monthUtilized[monthKey].total += res[key].total_count;
        monthUtilized[monthKey].available += res[key].available_count;
        monthUtilized[monthKey].notAvailable = res[key].not_available_count;
        monthUtilized[monthKey].unutilized += res[key].unutilized_count;
      }

      const datasetData = [];

      for (let index = 0; index < labels.length; index++) {
        const monthNumber = index;
        const label = labels[monthNumber];
        const monthStats = monthUtilized[monthNumber.toString()]

        if (!monthStats) {
          datasetData.push({
            label,
            averagePercentageOfUtilization: 0,
            ...createBlankDataPoint(),
          })
        } else {
          const averagePercentageOfUtilization = (
            (monthStats.utilized / monthStats.total) * 100
          ).toFixed(2)
          datasetData.push({
            label,
            averagePercentageOfUtilization,
            ...monthStats,
          });
        }
      }
      dataset.data = datasetData;

      const data = networkUtlisationData;
      data.datasets.push(dataset)

      return data
    } else if (range === IQueryFilterRange.ThisMonth || range === IQueryFilterRange.PrevMonth) {
      // Map of { date: { utilized, total } }
      const dayUtilized: { [key: string]: LineGraphUtilizationDataPoint } = {};
      const date = new Date(Object.keys(res)[0])
      const monthKey = date.getMonth()

      // first lets build the labels needed for the days in this month.
      let labels = []
      const daysInMonth = this.daysInGivenMonth(monthKey)
      for (let index = 0; index < daysInMonth; index++) {
        labels.push(index + 1)
      }

      for (const key of Object.keys(res)) {
        const date = new Date(key)
        const dayString = `${date.getDate()}`

        if (!dayUtilized[dayString]) {
          dayUtilized[dayString] = createBlankDataPoint()
        }

        dayUtilized[dayString].utilized += res[key].utilized_count
        dayUtilized[dayString].total = res[key].total_count
        dayUtilized[dayString].available += res[key].available_count;
        dayUtilized[dayString].notAvailable = res[key].not_available_count;
        dayUtilized[dayString].unutilized += res[key].unutilized_count;
      }
      const datasetData = [];

      for (let index = 0; index < labels.length; index++) {
        const dayString = index + 1;
        const dayStats = dayUtilized[dayString.toString()]
        if (!dayStats) {
          datasetData.push({
            label: dayString,
            averagePercentageOfUtilization: 0,
            ...createBlankDataPoint(),
          })
        } else {
          const averagePercentageOfUtilization = (
            (dayStats.utilized / dayStats.total) * 100
          ).toFixed(2)
          datasetData.push({
            label: dayString,
            averagePercentageOfUtilization,
            ...dayStats,
          });
        }
      }

      console.log({ datasetData });
      dataset.data = datasetData;

      const data = networkUtlisationData
      data.labels = labels;
      data.datasets.push(dataset)

      return data
    }

    return res;
  }


  daysInGivenMonth(month: number) {
    const year = new Date().getFullYear()
    var now = new Date(year, month, 0);
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  }

  getRegionLeagueTableStatsDepots(regionId: number, _queryParams: IQueryParameters): Promise<LeagueDepotData[]> {
    const res = this.rest.get<LeagueDepotData[]>(`${this.baseApiUrl}/utilization/league_table_statistics/region/${regionId}/depots/`, _queryParams ? _queryParams : undefined)
    return lastValueFrom(res)
  }

  getDepartmentLeagueTableStatsDepots(departmentId: number, _queryParams: IQueryParameters): Promise<LeagueDepotData[]> {
    const res = this.rest.get<LeagueDepotData[]>(`${this.baseApiUrl}/utilization/league_table_statistics/department/${departmentId}/depots/`, _queryParams ? _queryParams : undefined)
    return lastValueFrom(res)
  }

  // utilization/league_table_statistics/department/1/regions/
  getDepartmentLeagueTableStatsRegions(departmentId: number, _queryParams: IQueryParameters): Promise<LeagueRegionData[]> {
    const res = this.rest.get<LeagueRegionData[]>(`${this.baseApiUrl}/utilization/league_table_statistics/department/${departmentId}/regions/`, _queryParams ? _queryParams : undefined)
    return lastValueFrom(res)
  }

  // /utilization/depot_utilization_statistics/
  getDepotUtilizationStatistics(_queryParams: IQueryParameters): Promise<DepotUtilizationStatistics[]> {
    const res = this.rest.get<DepotUtilizationStatistics[]>(`${this.baseApiUrl}/utilization/depot_utilization_statistics/`, _queryParams ? _queryParams : undefined)
    return lastValueFrom(res)
  }

  refactorUntilizedData(stats: DepotUtilizationStatistics[]) {
    const grouped = stats.reduce((acc, item) => {
      // Check if the depot_id exists in the accumulator
      if (!acc[item.depot_id]) {
        // If not, initialize it
        acc[item.depot_id] = {
          depot_name: item.depot_name, // Assuming depot_name is the same for all entries
          totalUnutilizedCount: 0
        };
      }

      // Sum the unutilized_count for each depot_id
      acc[item.depot_id].totalUnutilizedCount += item.unutilized_count;
      acc[item.depot_id].snapshot_date = item.snapshot_date;

      return acc;
    }, {} as any);

    // Convert the accumulator object back to a flat array
    const result = Object.values(grouped);

    return result as DepotUtilizationStatistics[];
  }

  // /utilization/vehicle_utilization_statistics/
  getVehicleUtilization(_queryParams: IQueryParameters): Promise<VehicleUtilizationStatisticsResponse> {
    const res = this.rest.get<VehicleUtilizationStatisticsResponse>(`${this.baseApiUrl}/utilization/vehicle_utilization_statistics/`, _queryParams ? _queryParams : undefined)
    return lastValueFrom(res)
  }

  getUtilizationStatistics(_queryParams: IQueryParameters): Promise<any> {
    const res = this.rest.get<VehicleUtilizationStatisticsResponse>(`${this.baseApiUrl}/utilization/utilization_statistics/`, _queryParams ? _queryParams : undefined)
    return lastValueFrom(res)
  }

  refactoryVehicleUtilizationData(stats: VehicleUtilizationStatisticsResponse): VehicleUtilizationStatistics[] {
    return Object.keys(stats).map((item) => {
      let vStats = {} as VehicleUtilizationStatistics;

      vStats = stats[item];

      const { vehicle_id, ...rest } = vStats;
      return { ...rest, reg: `${item}` } as VehicleUtilizationStatistics;
    })
  }

  getUnutilisedVehiclesData(): Promise<VehicleData[]> {
    const unutilisedVehiclesData: VehicleData[] = [
      { region: 'YG69LXS', vehicleCount: 10, depot: 'DPD Dartford' },
      { region: 'YG69LXD', vehicleCount: 8, depot: 'DPD Guildford' },
      { region: 'DE73SLT', vehicleCount: 5, depot: 'DPD Stoke' },
      { region: 'DE73SLT', vehicleCount: 2, depot: 'DPD Bristol' },
    ];
    return Promise.resolve(unutilisedVehiclesData);
  }
}
